'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2, UserCheck, Lock } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import type { BulkPayload } from '@/types/bulk-action';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  useBulkDeleteUsersMutation,
  useBulkActivateUsersMutation,
  useBulkDeactivateUsersMutation
} from '../services/users.mutations';
import { USERS_HASH } from '../constants';
import type { User } from '../types';

interface UsersTableActionBarProps {
  table: Table<User>;
  getBulkPayload: () => BulkPayload;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function UsersTableActionBar({
  table,
  getBulkPayload,
  onComplete,
  isAllPagesSelected,
  totalCount
}: UsersTableActionBarProps) {
  const t = useTranslations('users');
  const { open: deleteOpen, onOpenChange: toggleDelete } = useHashDialog(
    USERS_HASH.bulkDelete
  );

  const bulkDeleteMutation = useBulkDeleteUsersMutation();
  const bulkActivateMutation = useBulkActivateUsersMutation();
  const bulkDeactivateMutation = useBulkDeactivateUsersMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeleteMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkDeleteMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    toggleDelete(false);
  }

  async function handleBulkActivate() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkActivateMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkActivateMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
  }

  async function handleBulkDeactivate() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeactivateMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkDeactivateMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleDelete(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleBulkActivate}
            disabled={bulkActivateMutation.isPending}
          >
            {bulkActivateMutation.isPending ? (
              <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
            ) : (
              <UserCheck className='me-1.5 h-3.5 w-3.5' />
            )}
            {t('bulkActivate')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleBulkDeactivate}
            disabled={bulkDeactivateMutation.isPending}
          >
            {bulkDeactivateMutation.isPending ? (
              <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
            ) : (
              <Lock className='me-1.5 h-3.5 w-3.5' />
            )}
            {t('bulkDeactivate')}
          </Button>
        </div>
      </div>

      {/* Bulk delete confirmation — #bulk-delete */}
      <AlertDialog open={deleteOpen} onOpenChange={toggleDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
